import { useEffect, useRef, useState } from 'react';
import { date as fmtDate } from '@/lib/format';

/**
 * Дата выбирается календарём, а не набирается руками.
 * Один месяц, без пресетов: пресеты нужны периоду, а здесь
 * одиночная дата (макет, блок «Календарь»).
 *
 * Наружу и внутрь — ISO `ГГГГ-ММ-ДД`; русский вид только на кнопке.
 */

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];
const WEEKDAYS = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'вс'];

const iso = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

function monthGrid(year: number, month: number): { d: Date; out: boolean }[] {
  const first = new Date(year, month, 1);
  // неделя начинается с понедельника
  const shift = (first.getDay() + 6) % 7;
  const days: { d: Date; out: boolean }[] = [];

  for (let i = 0; i < shift; i++) days.push({ d: new Date(year, month, 1 - shift + i), out: true });

  const last = new Date(year, month + 1, 0).getDate();
  for (let i = 1; i <= last; i++) days.push({ d: new Date(year, month, i), out: false });

  while (days.length % 7) days.push({ d: new Date(year, month, last + (days.length % 7)), out: true });

  return days;
}

interface DatePickerProps {
  value: string | null;
  onChange: (value: string) => void;
  alignRight?: boolean;
  placeholder?: string;
  disabled?: boolean;
}

export function DatePicker({
  value,
  onChange,
  alignRight,
  placeholder = 'выбрать дату',
  disabled,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const initial = value ? new Date(`${value}T00:00:00`) : new Date();
  const [cursor, setCursor] = useState({ y: initial.getFullYear(), m: initial.getMonth() });
  const wrap = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!wrap.current?.contains(e.target as Node)) setOpen(false);
    };
    const escape = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', escape);
    };
  }, [open]);

  const today = iso(new Date());
  const shift = (delta: number) =>
    setCursor(({ y, m }) => {
      const next = m + delta;
      if (next < 0) return { y: y - 1, m: 11 };
      if (next > 11) return { y: y + 1, m: 0 };
      return { y, m: next };
    });

  return (
    <div className="wrapctl" ref={wrap}>
      <div
        className={`ctl${open ? ' act' : ''}`}
        onClick={() => {
          if (disabled) return;
          if (value) {
            const d = new Date(`${value}T00:00:00`);
            setCursor({ y: d.getFullYear(), m: d.getMonth() });
          }
          setOpen((v) => !v);
        }}
      >
        <svg>
          <use href="#i-cal" />
        </svg>
        <span>{value ? fmtDate(value) : placeholder}</span>
      </div>

      {open ? (
        <div className={alignRight ? 'calwrap rt' : 'calwrap'}>
          <div>
            <div className="cals">
              <div className="cal">
                <div className="calh">
                  <button type="button" onClick={() => shift(-1)}>
                    ‹
                  </button>
                  <div className="m">
                    {MONTHS[cursor.m]} {cursor.y}
                  </div>
                  <button type="button" onClick={() => shift(1)}>
                    ›
                  </button>
                </div>
                <div className="cg">
                  {WEEKDAYS.map((w) => (
                    <div className="wd" key={w}>
                      {w}
                    </div>
                  ))}
                  {monthGrid(cursor.y, cursor.m).map(({ d, out }) => {
                    const ds = iso(d);
                    const classes = ['d'];
                    if (out) classes.push('out');
                    // одиночная дата — .one, скруглена целиком; .s — начало периода (кит 2.15)
                    if (ds === value) classes.push('one');
                    else if (ds === today) classes.push('today');
                    return (
                      <div
                        key={`${ds}-${out ? 'o' : 'i'}`}
                        className={classes.join(' ')}
                        onClick={() => {
                          onChange(ds);
                          setOpen(false);
                        }}
                      >
                        {d.getDate()}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function DateField({
  label,
  hint,
  ...rest
}: DatePickerProps & { label: string; hint?: string }) {
  return (
    <div className="field">
      <span className="lbl">{label}</span>
      <DatePicker {...rest} />
      {hint ? <span className="sub">{hint}</span> : null}
    </div>
  );
}
